/**
 * CartDrawer Component
 * Right slide-over drawer showing cart items, quantities, and checkout CTA
 */

import React from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Stack,
  Chip,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Delete as DeleteIcon,
  ShoppingCart as CartIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useCartStore, useCartItemsWithDetails } from '../../store/cart';
import { formatPrice } from '../../lib/catalog';

interface CartDrawerProps {
  open: boolean;
  onClose: () => void;
}

const CartDrawer: React.FC<CartDrawerProps> = ({ open, onClose }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const items = useCartItemsWithDetails();
  const { setQty, remove, clear, getTotal, hasSubscription, hasMixedTypes } = useCartStore();

  const total = getTotal();
  const hasItems = items.length > 0;

  const handleProceedToCheckout = () => {
    onClose();
    navigate('/checkout');
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'subscription':
        return 'Monthly';
      case 'metered':
        return 'Per Hour';
      default:
        return 'One-time';
    }
  };

  const getTypeColor = (type: string): 'primary' | 'secondary' | 'info' => {
    switch (type) {
      case 'subscription':
        return 'secondary';
      case 'metered':
        return 'info';
      default:
        return 'primary';
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 400 },
          maxWidth: '100vw',
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CartIcon color="primary" />
            <Typography variant="h6">Your Cart</Typography>
            {hasItems && (
              <Chip
                label={items.length}
                size="small"
                color="primary"
                sx={{ ml: 1 }}
              />
            )}
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Cart Items */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
          {!hasItems ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                color: 'text.secondary',
              }}
            >
              <CartIcon sx={{ fontSize: 64, mb: 2, opacity: 0.3 }} />
              <Typography variant="h6" gutterBottom>
                Your cart is empty
              </Typography>
              <Typography variant="body2" textAlign="center">
                Add products from our pricing page to get started
              </Typography>
              <Button
                variant="outlined"
                onClick={() => {
                  onClose();
                  navigate('/pricing');
                }}
                sx={{ mt: 3 }}
              >
                View Pricing
              </Button>
            </Box>
          ) : (
            <List disablePadding>
              {items.map((item) => (
                <ListItem
                  key={item.skuId}
                  sx={{
                    bgcolor: alpha(theme.palette.background.default, 0.5),
                    borderRadius: 1,
                    mb: 1,
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                  }}
                >
                  <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography variant="subtitle2" fontWeight="medium">
                          {item.sku.name}
                        </Typography>
                        <Chip
                          label={getTypeLabel(item.sku.type)}
                          size="small"
                          color={getTypeColor(item.sku.type)}
                          sx={{ height: 20, fontSize: '0.65rem' }}
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {formatPrice(item.sku.unitAmount)}
                        {item.sku.type === 'subscription' && '/mo'}
                        {item.sku.type === 'metered' && '/hr'}
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={() => remove(item.skuId)}
                      sx={{ color: 'error.main' }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>

                  {/* Quantity controls for metered items */}
                  {item.sku.type === 'metered' && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                      <IconButton
                        size="small"
                        onClick={() => setQty(item.skuId, item.qty - 1)}
                        disabled={item.qty <= 1}
                      >
                        <RemoveIcon fontSize="small" />
                      </IconButton>
                      <Typography variant="body2" sx={{ minWidth: 40, textAlign: 'center' }}>
                        {item.qty} hr{item.qty > 1 ? 's' : ''}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => setQty(item.skuId, item.qty + 1)}
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )}

                  <Typography
                    variant="subtitle2"
                    fontWeight="bold"
                    sx={{ mt: 1, alignSelf: 'flex-end' }}
                  >
                    {formatPrice(item.subtotal)}
                    {item.sku.type === 'subscription' && '/mo'}
                  </Typography>
                </ListItem>
              ))}
            </List>
          )}
        </Box>

        {/* Footer with totals and CTA */}
        {hasItems && (
          <Box sx={{ borderTop: `1px solid ${theme.palette.divider}`, p: 2 }}>
            {hasMixedTypes() && (
              <Box
                sx={{
                  bgcolor: alpha(theme.palette.warning.main, 0.1),
                  borderRadius: 1,
                  p: 1.5,
                  mb: 2,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Your cart contains both one-time and subscription items.
                  You'll complete two checkout steps.
                </Typography>
              </Box>
            )}

            <Stack spacing={1} sx={{ mb: 2 }}>
              {/* One-time total */}
              {items.filter(i => i.sku.type !== 'subscription').length > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    One-time charges:
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {formatPrice(
                      items
                        .filter(i => i.sku.type !== 'subscription')
                        .reduce((sum, i) => sum + i.subtotal, 0)
                    )}
                  </Typography>
                </Box>
              )}

              {/* Subscription total */}
              {hasSubscription() && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    Monthly recurring:
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {formatPrice(
                      items
                        .filter(i => i.sku.type === 'subscription')
                        .reduce((sum, i) => sum + i.subtotal, 0)
                    )}/mo
                  </Typography>
                </Box>
              )}

              <Divider sx={{ my: 1 }} />

              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="h6">Total Due Today:</Typography>
                <Typography variant="h6" fontWeight="bold" color="primary">
                  {formatPrice(total)}
                </Typography>
              </Box>
            </Stack>

            <Button
              variant="contained"
              fullWidth
              size="large"
              onClick={handleProceedToCheckout}
              sx={{ mb: 1 }}
            >
              Proceed to Checkout
            </Button>

            <Button
              variant="text"
              fullWidth
              size="small"
              color="inherit"
              onClick={clear}
            >
              Clear Cart
            </Button>
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default CartDrawer;
